import React, { useEffect, useMemo, useState } from 'react'
import View from './AddressForm.view'
import { Address, AddressFormMode, AddressFormProps } from './AddressForm.types'
import { useCEPFetcher } from 'core/logic/cep/cep.hook'
import { validateCEP } from 'core/helpers/validators'
import { useFieldErrors } from 'features/CVM88/Form/Form.hook'
import { AddressFormField } from './AddressForm.types'
import { t, tO } from 'translate/i18n'
import Button from '@onepercentio/one-ui/components/Button'
import { useAddressManagement } from 'context/User'
import Loader from '@onepercentio/one-ui/components/Loader'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import { AddressSchema } from '@onepercentio/rarum-types/_shared'
import { AddressFormTestIds } from './AddressForm.e2e'

export const FormFields: (
  lockFields: ReturnType<typeof useCEPFetcher>[0],
  control: {
    error?: any
    loading: boolean
    retry: () => void
  }
) => AddressFormField[] = (lockFields, control) => [
  {
    id: 'zipCode',
    type: 'text',
    title: t('authenticated.userProfile.profileForm.cep'),
    mask: '99999-999',
    disabled: false,
    error: control.error
      ? {
          label: t('authenticated.userProfile.error'),
          retry: control.retry,
        }
      : undefined,
  },
  {
    id: 'state',
    type: 'select',
    title: t('authenticated.userProfile.profileForm.state'),
    disabled: lockFields.state,
    options: [
      ['Acre', 'AC'],
      ['Alagoas', 'AL'],
      ['Amapá', 'AP'],
      ['Amazonas', 'AM'],
      ['Bahia', 'BA'],
      ['Ceará', 'CE'],
      ['Espírito Santo', 'ES'],
      ['Goiás', 'GO'],
      ['Maranhão', 'MA'],
      ['Mato Grosso', 'MT'],
      ['Mato Grosso do Sul', 'MS'],
      ['Minas Gerais', 'MG'],
      ['Pará', 'PA'],
      ['Paraíba', 'PB'],
      ['Paraná', 'PR'],
      ['Pernambuco', 'PE'],
      ['Piauí', 'PI'],
      ['Rio de Janeiro', 'RJ'],
      ['Rio Grande do Norte', 'RN'],
      ['Rio Grande do Sul', 'RS'],
      ['Rondônia', 'RO'],
      ['Roraima', 'RR'],
      ['Santa Catarina', 'SC'],
      ['São Paulo', 'SP'],
      ['Sergipe', 'SE'],
      ['Tocantins', 'TO'],
      ['Distrito Federal', 'DF'],
    ].map(([name, code]) => ({
      label: name,
      value: code,
    })),
  },
  {
    id: 'city',
    type: 'text',
    title: t('authenticated.userProfile.profileForm.city'),
    disabled: lockFields.city,
  },
  {
    id: 'neighborhood',
    type: 'text',
    title: t('authenticated.userProfile.profileForm.neighbourhood'),
    disabled: lockFields.neighbourhood,
  },
  {
    id: 'street',
    type: 'text',
    title: t('authenticated.userProfile.profileForm.address'),
    disabled: lockFields.address,
  },
  {
    id: 'number',
    type: 'text',
    title: t('authenticated.userProfile.profileForm.number'),
    disabled: control.loading,
  },
  {
    id: 'complement',
    type: 'text',
    title: t('authenticated.userProfile.profileForm.complement'),
    disabled: false,
    optional: true,
  },
]

export default function AddressFormLogic({
  address: predefAddress,
  ...props
}: AddressFormProps) {
  const [address, setAddress] = useState<Address>(predefAddress)
  const { zipCode } = address

  const [lockFields, fetchingCEP, cepResult, cepFetchError, fetchCepAgain] =
    useCEPFetcher({
      cep: zipCode,
      enable: props.mode === AddressFormMode.WRITE && !!validateCEP(zipCode),
    })

  useEffect(() => {
    if (cepResult) {
      setAddress((prev) => ({
        ...prev,
        city: cepResult.cidade!,
        state: cepResult.estado!,
        street: cepResult.logradouro! || prev.street,
        neighborhood: cepResult.bairro! || prev.neighborhood,
      }))
    }
  }, [cepResult])

  const isAddressComplete = useMemo(() => {
    return (
      !!address.zipCode &&
      !!address.state &&
      !!address.city &&
      !!address.street &&
      !!address.number &&
      !!address.neighborhood
    )
  }, [address])

  const errors = useFieldErrors(
    [
      { id: 'zipCode', type: 'number' },
      { id: 'street', type: 'text' },
      { id: 'number', type: 'text' },
      { id: 'city', type: 'text' },
      { id: 'complement', type: 'text', optional: true },
      { id: 'neighborhood', type: 'text' },
      { id: 'state', type: 'text' },
    ] as const,
    address as AddressSchema
  )

  useEffect(() => {
    if (props.mode === AddressFormMode.WRITE)
      props.onAddress(address, isAddressComplete, errors)
  }, [isAddressComplete, address, props.mode])

  const IntlFormFields = useMemo(() => {
    return FormFields(lockFields, {
      loading: fetchingCEP,
      error: cepFetchError,
      retry: fetchCepAgain,
    })
  }, [lockFields, fetchingCEP, cepFetchError])

  return (
    <View
      onAddressFieldChange={(_t, id, val) =>
        setAddress((prev) => ({
          ...prev,
          [id]: val,
        }))
      }
      address={address}
      mode={props.mode}
      errors={'showErrors' in props && props.showErrors ? errors : {}}
      fields={IntlFormFields}
    />
  )
}

export function AddressCreation({
  onCancel,
  onCreated,
}: ClickActions<'onCreated' | 'onCancel'>) {
  const [[address, isComplete], setAddress] = useState<
    Parameters<
      (AddressFormProps & { mode: AddressFormMode.WRITE })['onAddress']
    >
  >([{} as Address, false, {}])
  const { createAddress, loading } = useAddressManagement()
  return (
    <>
      <AddressFormLogic
        address={address}
        mode={AddressFormMode.WRITE}
        onAddress={(...params) => {
          setAddress(params)
        }}
        showErrors={false}
      />
      <Spacing size='stretch' />
      <Button
        onClick={() => createAddress(address).then(() => onCreated())}
        disabled={loading || !isComplete}
        variant='filled'
        data-testid={AddressFormTestIds.SAVE}>
        {loading ? (
          <Loader />
        ) : (
          tO('offer.creation.steps.address.actions.addAddress')
        )}
      </Button>
      <Spacing size='8' />
      <Button variant='outline' onClick={onCancel}>
        {t('generic.cancel')}
      </Button>
    </>
  )
}
